import getEnvironment from '@/utils/env'
import { useScriptTag } from '@vueuse/core'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type UserGuidingApi = any

declare global {
  interface Window {
    userGuidingLayer?: unknown[]
    userGuiding?: UserGuidingApi
  }
}

const ugScript = useScriptTag(
  `https://static.userguiding.com/media/user-guiding-${import.meta.env.VITE_USERGUIDING_CONTAINER_ID}-embedded.js`,
  undefined,
  {
    immediate: false,
    async: true,
  }
)

let isInitialized = false

/**
 * Initialize UserGuiding.
 * Sets up the stub and loads the script in production/test environments.
 */
export function initUserGuiding(): void {
  const environment = getEnvironment()
  const forceEnable = import.meta.env.VITE_TEST_EXTERNAL_SCRIPTS === 'true'

  // Only enable in production and test environments (unless forced)
  if (!forceEnable && (environment == null || !['production', 'test'].includes(environment.id))) {
    return
  }

  if (isInitialized) {
    return
  }

  // Initialize the queue layer
  window.userGuidingLayer = window.userGuidingLayer ?? []

  // Set up the stub that queues method calls until the script loads
  if (window.userGuiding == null) {
    const ug: UserGuidingApi = { q: [] }

    ug.c = function (methodName: string) {
      return function () {
        // eslint-disable-next-line prefer-rest-params
        ug.q.push([methodName, arguments])
      }
    }

    const methods = ['previewGuide', 'finishPreview', 'track', 'identify', 'hideChecklist', 'launchChecklist']
    for (const method of methods) {
      ug[method] = ug.c(method)
    }

    window.userGuiding = ug
  }

  void ugScript.load().catch(() => {
    /* a blocked CDN (adblocker, offline) is tolerated: the stub keeps queueing calls */
  })

  isInitialized = true
}
initUserGuiding()

type UserGuidingAttributes = Record<string, string | boolean | number | undefined | null>

/**
 * Identify the current user to UserGuiding.
 * Call this after user authentication.
 */
export function identifyUserGuidingUser(userId: string, attributes?: UserGuidingAttributes): void {
  if (window.userGuiding == null) {
    return
  }

  const environment = getEnvironment()
  const filteredAttributes: Record<string, string> = {
    environment: environment?.id ?? 'unknown',
  }

  if (attributes != null) {
    for (const [key, value] of Object.entries(attributes)) {
      if (value == null) continue
      filteredAttributes[key] = String(value)
    }
  }

  window.userGuiding.identify(userId, filteredAttributes)
}

/**
 * Track a custom event in UserGuiding.
 */
export function trackUserGuidingEvent(eventName: string, attributes?: Record<string, string>): void {
  window.userGuiding?.track(eventName, attributes)
}
